/**
 * URL Compression Utilities for Inbox Filters
 *
 * Compresses filter parameters using gzip + base64 encoding to support
 * more filters within URL length limits (WAF ~2000 bytes, CloudFront ~8192 bytes)
 *
 * With compression: ~70 GUIDs fit in 2000 bytes, ~290 GUIDs in 8192 bytes
 */

/**
 * Threshold in bytes for determining when to compress filters
 *
 * Compress when uncompressed JSON exceeds this threshold to stay safely under
 * the WAF 2000 byte limit after base64 encoding and URL parameter overhead.
 *
 * Base64 encoding increases size by ~33%, and URL encoding adds additional overhead.
 * By compressing at 1000 bytes, we ensure the final URL parameter stays well
 * under the 2000 byte WAF limit even after encoding.
 */
const COMPRESSION_THRESHOLD_BYTES = 1000;

/**
 * Helper to read all chunks from a ReadableStream
 */
async function readStream(
  stream: ReadableStream<Uint8Array>,
): Promise<Uint8Array> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  // Combine all chunks into a single Uint8Array
  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result;
}

/**
 * Converts Uint8Array to URL-safe base64 string
 */
function toBase64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Compresses a JSON object to a base64-encoded string using gzip
 * @param data - Object to compress
 * @returns Base64-encoded compressed string
 */
export async function compressFilters(
  data: Record<string, unknown>,
): Promise<string> {
  const jsonString = JSON.stringify(data);
  const input = new TextEncoder().encode(jsonString);

  // Use CompressionStream API if available (modern browsers)
  if (typeof CompressionStream !== 'undefined') {
    const stream = new CompressionStream('gzip');
    const writer = stream.writable.getWriter();

    writer.write(input);
    writer.close();

    const compressed = await readStream(stream.readable);
    return toBase64Url(compressed);
  }

  // Fallback: base64 encode without compression
  return toBase64Url(input);
}

/**
 * Converts URL-safe base64 string to Uint8Array
 */
function fromBase64Url(base64Url: string): Uint8Array {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

  const binaryString = atob(padded);
  const buffer = new ArrayBuffer(binaryString.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

/**
 * Decompresses a base64-encoded gzip string back to a JSON object
 * @param compressed - Base64-encoded compressed string
 * @returns Decompressed object
 */
export async function decompressFilters(
  compressed: string,
): Promise<Record<string, unknown>> {
  if (!compressed) {
    throw new Error('Compressed string is empty');
  }

  const bytes = fromBase64Url(compressed);

  // Use DecompressionStream API if available
  if (typeof DecompressionStream !== 'undefined') {
    try {
      const stream = new DecompressionStream('gzip');
      const writer = stream.writable.getWriter();

      // Ensure bytes has proper ArrayBuffer backing for writer.write()
      // Create a new ArrayBuffer to ensure it's not a SharedArrayBuffer
      const buffer = new ArrayBuffer(bytes.length);
      const view = new Uint8Array(buffer);
      view.set(bytes);
      writer.write(view);
      writer.close();

      const decompressed = await readStream(stream.readable);
      const jsonString = new TextDecoder().decode(decompressed);
      return JSON.parse(jsonString);
    } catch (error) {
      try {
        const jsonString = new TextDecoder().decode(bytes);
        return JSON.parse(jsonString);
      } catch {
        throw new Error(`Failed to decompress filters: ${error}`);
      }
    }
  }

  // Fallback: Try parsing as uncompressed base64
  try {
    const jsonString = new TextDecoder().decode(bytes);
    return JSON.parse(jsonString);
  } catch (error) {
    throw new Error(`Failed to decompress filters: ${error}`);
  }
}

/**
 * Checks if filters should be compressed based on estimated URL length
 * @param filters - Filter object to check
 * @returns true if compression is recommended
 */
export function shouldCompressFilters(
  filters: Record<string, unknown>,
): boolean {
  const estimatedLength = JSON.stringify(filters).length;
  return estimatedLength > COMPRESSION_THRESHOLD_BYTES;
}

/**
 * Estimates the compressed size of filters
 * @param filters - Filter object
 * @returns Estimated size in bytes
 */
export async function estimateCompressedSize(
  filters: Record<string, unknown>,
): Promise<number> {
  try {
    const compressed = await compressFilters(filters);
    // Base64 encoding increases size by ~33%, but gzip typically reduces by 60-80%
    // For estimation, we'll use the actual compressed length
    return compressed.length;
  } catch {
    // Fallback: estimate based on JSON string length
    return JSON.stringify(filters).length;
  }
}
